<template>
    <div class="signup">
        <h1>Create your MyStekz account</h1>

        <Message v-if="tokenValidating" :closable="false" severity="info">
            Validating invitation...
        </Message>

        <Message v-if="tokenError" :closable="false" severity="error">
            {{ tokenError }}
        </Message>

        <Message
            v-if="isInvitation && !tokenError"
            :closable="false"
            severity="info"
        >
            You've been invited to join <strong>{{ invitationOrgName }}</strong>
        </Message>

        <Message v-if="errorMessage" :closable="false" severity="error">
            {{ errorMessage }}
        </Message>

        <!-- Show expired token guidance instead of signup form -->
        <div v-if="tokenError" class="expired-token-info">
            <p>This invitation link is no longer valid.</p>
            <p>
                Please contact your administrator to request a new invitation.
            </p>
            <div class="buttons">
                <Button
                    class="p-button-raised button"
                    label="Back to Login"
                    @click="router.push('/login')"
                />
            </div>
        </div>

        <div v-else-if="showEmailPasswordForm" class="signup-form">
            <form @submit.prevent="handleSignUp">
                <div class="input email">
                    <label for="email">Email</label>
                    <InputText
                        id="email"
                        v-model="email"
                        type="email"
                        autocomplete="email"
                        :class="{ 'p-invalid': submitted && !email }"
                        :disabled="isInvitation"
                        aria-describedby="email-error"
                    />
                    <small
                        id="email-error"
                        class="p-error"
                        v-if="submitted && !email"
                    >
                        Email is required
                    </small>
                </div>
                <div class="input password">
                    <label for="password">Password</label>
                    <Password
                        id="password"
                        v-model="password"
                        :inputProps="{
                            autocomplete: 'new-password',
                        }"
                        :class="{ 'p-invalid': submitted && !password }"
                        :feedback="true"
                        aria-describedby="password-error"
                        toggleMask
                    />
                    <small
                        id="password-error"
                        class="p-error"
                        v-if="submitted && !password"
                    >
                        Password is required
                    </small>
                </div>
                <div class="input password">
                    <label for="confirmPassword">Confirm Password</label>
                    <Password
                        id="confirmPassword"
                        v-model="confirmPassword"
                        :inputProps="{
                            autocomplete: 'new-password',
                        }"
                        :class="{
                            'p-invalid':
                                (submitted && !confirmPassword) ||
                                (submitted && password !== confirmPassword),
                        }"
                        :feedback="false"
                        aria-describedby="confirm-password-error"
                        toggleMask
                    />
                    <small
                        id="confirm-password-error"
                        class="p-error"
                        v-if="submitted && !confirmPassword"
                    >
                        Please confirm your password
                    </small>
                    <small
                        id="password-match-error"
                        class="p-error"
                        v-if="submitted && password !== confirmPassword"
                    >
                        Passwords do not match
                    </small>
                </div>
                <div class="input name">
                    <label for="name">Full Name</label>
                    <InputText
                        id="name"
                        v-model="name"
                        type="text"
                        autocomplete="name"
                        :class="{ 'p-invalid': submitted && !name }"
                        aria-describedby="name-error"
                    />
                    <small
                        id="name-error"
                        class="p-error"
                        v-if="submitted && !name"
                    >
                        Name is required
                    </small>
                </div>

                <div class="buttons">
                    <Button
                        type="submit"
                        class="p-button-raised button"
                        label="Create Account"
                        :loading="loading"
                    />
                    <div class="login-link">
                        Already have an account?
                        <router-link to="/login">Sign in</router-link>
                    </div>
                </div>
            </form>
        </div>

        <div v-else-if="!tokenError" class="signup-options">
            <p>Choose how to create your account</p>
            <div class="buttons">
                <Button
                    class="p-button-raised button"
                    label="Sign up with Google"
                    @click="handleGoogleSignUp"
                    :loading="loading"
                >
                    <template #icon>
                        <img
                            class="google-icon"
                            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                            alt="Google icon"
                        />
                    </template>
                </Button>
                <Button
                    class="p-button-raised button"
                    label="Sign up with Email"
                    @click="showEmailPasswordForm = true"
                    :loading="loading"
                >
                    <template #icon>
                        <img
                            class="mail-icon"
                            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/mail.svg"
                            alt="Email icon"
                        />
                    </template>
                </Button>
                <div class="login-link">
                    Already have an account?
                    <router-link to="/login">Sign in</router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useServices } from "@/lib/openapi";
import {
    AuthError,
    useAuthenticationStore,
} from "@/stores/authentication-store";

const { signupUserWithGoogle, signupUserWithEmailAndPassword } =
    useAuthenticationStore();
const router = useRouter();
const route = useRoute();

const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const name = ref("");
const errorMessage = ref("");
const loading = ref(false);
const submitted = ref(false);
const showEmailPasswordForm = ref(false);

// Invitation-related state
const invitationToken = ref<string | null>(null);
const invitationEmail = ref<string | null>(null);
const invitationOrgName = ref<string | null>(null);
const isInvitation = ref(false);
const tokenValidating = ref(false);
const tokenError = ref("");

// Validate invitation token on mount
onMounted(async () => {
    const token = route.query.token as string | undefined;
    if (token) {
        invitationToken.value = token;
        tokenValidating.value = true;
        tokenError.value = "";

        try {
            const api = useServices({
                opts: { throwOnError: false },
                requires: { auth: false, organization: false, stek: false },
            });

            const response =
                await api.validateInvitationUserInvitationValidatePost({
                    body: { token },
                });

            if (response.data) {
                isInvitation.value = true;
                invitationEmail.value = response.data.email;
                invitationOrgName.value = response.data.organization_name;
                email.value = response.data.email;
            } else if (response.error) {
                tokenError.value =
                    response.error.detail ||
                    "Invalid or expired invitation token";
            }
        } catch (error) {
            console.error("Token validation error:", error);
            tokenError.value =
                error instanceof Error
                    ? `Failed to validate invitation token: ${error.message}`
                    : "Failed to validate invitation token";
        } finally {
            tokenValidating.value = false;
        }
    }
});

const handleSignUp = async () => {
    submitted.value = true;

    if (
        !email.value ||
        !password.value ||
        !confirmPassword.value ||
        !name.value ||
        password.value !== confirmPassword.value
    ) {
        return;
    }

    loading.value = true;
    errorMessage.value = "";

    try {
        await signupUserWithEmailAndPassword(
            email.value,
            password.value,
            name.value,
            isInvitation.value
                ? (invitationToken.value ?? undefined)
                : undefined
        );
        router.push("/");
    } catch (error) {
        if (error instanceof AuthError) {
            errorMessage.value = error.message;
        } else {
            throw error;
        }
    } finally {
        loading.value = false;
    }
};

const handleGoogleSignUp = async () => {
    loading.value = true;
    errorMessage.value = "";

    try {
        await signupUserWithGoogle(
            isInvitation.value
                ? (invitationToken.value ?? undefined)
                : undefined,
            isInvitation.value
                ? (invitationEmail.value ?? undefined)
                : undefined
        );
        router.push("/");
    } catch (error) {
        if (error instanceof AuthError) {
            errorMessage.value = error.message;
        } else {
            throw error;
        }
    } finally {
        loading.value = false;
    }
};
</script>

<style lang="scss" scoped>
.signup {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    padding: 30px;
    align-items: center;
    color: var(--stekz-white);
    background-color: var(--stekz-primary-color);

    h1 {
        color: var(--stekz-white);
        margin-bottom: 2rem;
    }

    .p-message {
        margin-bottom: 2rem;
    }

    .signup-form,
    .signup-options,
    .expired-token-info {
        max-width: 320px;
        width: 100%;
    }

    .signup-options {
        text-align: center;

        p {
            color: var(--stekz-white);
            margin-bottom: 1.5rem;
            font-size: 1.1rem;
        }
    }

    .expired-token-info {
        text-align: center;

        p {
            color: var(--stekz-white);
            margin-bottom: 1rem;
            font-size: 1rem;
            line-height: 1.5;

            &:first-of-type {
                font-weight: 500;
                font-size: 1.1rem;
            }
        }

        .buttons {
            margin-top: 2rem;
        }
    }
}

.input {
    margin-bottom: 1.5rem;

    &.email,
    &.password,
    &.name {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    &.checkbox {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 0.75rem;
        margin-bottom: 0.5rem;

        .checkbox-label {
            font-size: 0.875rem;
            line-height: 1.4;
            color: var(--stekz-white);

            a {
                color: var(--stekz-dev-color);
                text-decoration: underline;

                &:hover {
                    text-decoration: none;
                }
            }
        }
    }

    label {
        font-weight: 500;
    }

    .p-password {
        width: 100%;
    }
}

.buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-top: 2rem;
}

.button {
    background-color: var(--stekz-dev-color);
    color: var(--stekz-white);
    width: 100%;

    &.button-back {
        background-color: transparent;
        border-color: var(--stekz-white);
        color: var(--stekz-white);
    }
}

.login-link {
    text-align: center;
    margin-top: 0.5rem;

    a {
        color: var(--stekz-white);
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }
}

.google-icon,
.mail-icon {
    border: none;
    display: inline-block;
    height: 18px;
    vertical-align: middle;
    width: 18px;
    margin-right: 8px;
}

:deep(.p-password-input) {
    width: 100%;
}
</style>
